// IR history drawer for the project page. Lists past IR revisions and lets
// the user revert. Reverting saves the current IR as a new revision first so
// nothing is lost.

import { useEffect, useState } from 'react';
import { ApiError, irHistory, type IrRevisionSummary } from '../api.js';
import type { Ir } from '@scriptvoice/ir';

interface Props {
  projectId: string;
  onClose: () => void;
  onReverted: (ir: Ir) => void;
}

const CHANGE_LABELS: Record<IrRevisionSummary['change_type'], string> = {
  direct: 'directed',
  edit: 'edited',
  redirect_section: 'redirected section',
  revert: 'reverted',
  import: 'imported',
};

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

export function HistoryDrawer({ projectId, onClose, onReverted }: Props) {
  const [revisions, setRevisions] = useState<IrRevisionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reverting, setReverting] = useState<string | null>(null);

  async function refresh() {
    setError(null);
    try {
      const r = await irHistory.list(projectId);
      setRevisions(r.revisions);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load history.');
      setRevisions([]);
    }
  }

  useEffect(() => {
    void refresh();
  }, [projectId]);

  async function handleRevert(revisionId: string, revisionNumber: number) {
    if (!confirm(`Revert to revision ${revisionNumber}? Your current IR will be preserved as a new revision first.`)) return;
    setReverting(revisionId);
    setError(null);
    try {
      const r = await irHistory.revert(projectId, revisionId);
      onReverted(r.revision.ir);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Revert failed.');
    } finally {
      setReverting(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex">
      <div
        className="flex-1 bg-ink/30 backdrop-blur-sm"
        onClick={onClose}
        aria-label="close history"
      />
      <div className="w-full max-w-md bg-parchment border-l border-soft shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-parchment/95 border-b border-soft px-5 py-4 flex items-baseline justify-between">
          <h2 className="font-serif text-xl">History</h2>
          <button onClick={onClose} className="text-xs text-muted hover:text-ink">
            close
          </button>
        </div>

        <div className="px-5 py-4">
          {error && (
            <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2 mb-3">
              {error}
            </div>
          )}

          {revisions === null ? (
            <p className="text-muted text-sm">loading…</p>
          ) : revisions.length === 0 ? (
            <p className="text-muted text-sm">No history yet — direct the script to start.</p>
          ) : (
            <ul className="space-y-2">
              {revisions.map((r, i) => {
                const isLatest = i === 0;
                return (
                  <li
                    key={r.id}
                    className={`border rounded-md px-4 py-3 ${
                      isLatest ? 'border-accent/40 bg-accent/5' : 'border-soft bg-white'
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm">v{r.revision_number}</span>
                          <span className="text-xs text-muted">{CHANGE_LABELS[r.change_type]}</span>
                          {isLatest && (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-accent/10 text-accent rounded">
                              current
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted/80 mt-1">{fmtTime(r.created_at)}</div>
                        {r.director_provider && r.director_model && (
                          <div className="text-[11px] text-muted/70 mt-0.5">
                            {r.director_provider}/{r.director_model}
                          </div>
                        )}
                        {r.change_summary && (
                          <p className="text-xs text-muted mt-1.5">{r.change_summary}</p>
                        )}
                      </div>
                      {!isLatest && (
                        <button
                          onClick={() => handleRevert(r.id, r.revision_number)}
                          disabled={reverting !== null}
                          className="text-xs text-muted hover:text-ink px-2 py-1 disabled:opacity-40 shrink-0"
                        >
                          {reverting === r.id ? 'reverting…' : 'revert'}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
